import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const AppPreferencesContext = createContext(null);

const THEME_STORAGE_KEY = "ac_admin_theme_preference";
const DEFAULT_THEME = "system";
const SUPPORTED_THEMES = ["system", "light", "dark"];

// Fallback-Texte
const FALLBACK_TEXT = {
  "shell.dashboard": "Dashboard",
  "shell.usersClasses": "Nutzer:innen & Klassen",
  "shell.content": "Inhalte",
  "shell.stats": "Statistiken",
  "shell.notifications": "Benachrichtigungen",
  "shell.feedback": "Feedback",
  "shell.settings": "Einstellungen",
  "shell.logout": "Abmelden",
  "shell.administration": "Administration",
  "shell.adminPanel": "Admin Panel",
  "shell.sidebar": "Menü",
  "shell.profile": "Profil",
  "shell.tagline": "",
  "shell.overview": "Übersicht",
  "shell.users": "Nutzer:innen",
  "shell.classes": "Klassen",
  "shell.tips": "Tipps",
  "shell.challenges": "Challenges",
  "shell.rules": "Regeln",
  "shell.myProfile": "Mein Profil",

  "shell.notificationsMenuTitle": "Benachrichtigungen",
  "shell.notificationsMenuSubtitle": "Aktuelle Hinweise im Überblick",
  "shell.noNewNotifications": "Keine neuen Benachrichtigungen",
  "shell.viewAllNotifications": "Alle Benachrichtigungen ansehen",
  "shell.markAllRead": "Alle als gelesen markieren",
  "shell.newNotificationSingular": "{{count}} neue Benachrichtigung",
  "shell.newNotificationPlural": "{{count}} neue Benachrichtigungen",

  "shell.defaultNotifications.feedbackTag": "Neu",
  "shell.defaultNotifications.feedbackTitle": "Neues Feedback eingegangen",
  "shell.defaultNotifications.feedbackSub":
    "Ein neuer Rückmeldungseintrag wartet auf Prüfung.",

  "shell.defaultNotifications.streakTag": "Streak",
  "shell.defaultNotifications.streakTitle": "Streak-Entwicklung auffällig",
  "shell.defaultNotifications.streakSub":
    "Mehrere Nutzer:innen haben zuletzt ihre Gewohnheiten unterbrochen.",

  "shell.defaultNotifications.stressTag": "Belastung",
  "shell.defaultNotifications.stressTitle": "Erhöhte Stresswerte erkannt",
  "shell.defaultNotifications.stressSub":
    "Bei einzelnen Profilen wurden zuletzt höhere Stresswerte erfasst.",

  "commonActions.backToOverview": "Zurück zur Übersicht",
  "commonActions.backToStats": "Zurück zu Statistiken",
  "commonActions.backToUsers": "Zurück zu Nutzer:innen",
  "common.live": "Live",
  "common.hide": "Verbergen",
  "common.show": "Anzeigen",
  "common.cancel": "Abbrechen",
  "common.add": "Hinzufügen",
  "common.remove": "Entfernen",

  "commonTime.today": "Heute",
  "commonTime.daysAgo": "vor {{count}} Tagen",

  "commonRanges.today": "Heute",
  "commonRanges.weekly": "Wöchentlich",
  "commonRanges.monthly": "Monatlich",
  "commonRanges.yearly": "Jährlich",
};

function readStoredValue(key, allowedValues, fallbackValue) {
  const storedValue = localStorage.getItem(key);
  return allowedValues.includes(storedValue) ? storedValue : fallbackValue;
}

function getSystemTheme() {
  if (typeof window === "undefined" || !window.matchMedia) {
    return "light";
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function interpolate(template, replacements = {}) {
  if (typeof template !== "string") {
    return template;
  }

  return template.replace(/\{(\w+)\}|\{\{(\w+)\}\}/g, (_, singleKey, doubleKey) => {
    const key = singleKey || doubleKey;
    return replacements[key] != null ? String(replacements[key]) : `{${key}}`;
  });
}

export function AppPreferencesProvider({ children }) {
  const [themePreference, setThemePreferenceState] = useState(() =>
    readStoredValue(THEME_STORAGE_KEY, SUPPORTED_THEMES, DEFAULT_THEME)
  );

  const [systemTheme, setSystemTheme] = useState(getSystemTheme);

  const resolvedTheme = useMemo(() => {
    return themePreference === "system" ? systemTheme : themePreference;
  }, [themePreference, systemTheme]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleThemeChange = (event) => {
      setSystemTheme(event.matches ? "dark" : "light");
    };

    mediaQuery.addEventListener("change", handleThemeChange);

    return () => {
      mediaQuery.removeEventListener("change", handleThemeChange);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, themePreference);
  }, [themePreference]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    document.documentElement.setAttribute("data-theme", resolvedTheme);
  }, [resolvedTheme]);

  const t = useCallback((key, replacements = {}) => {
    const template = FALLBACK_TEXT[key] ?? key;
    return interpolate(template, replacements);
  }, []);

  const setThemePreference = useCallback((nextTheme) => {
    if (!SUPPORTED_THEMES.includes(nextTheme)) {
      return;
    }

    setThemePreferenceState(nextTheme);
  }, []);

  const value = useMemo(
    () => ({
      themePreference,
      resolvedTheme,
      setThemePreference,
      themeOptions: SUPPORTED_THEMES,
      t,
    }),
    [themePreference, resolvedTheme, setThemePreference, t]
  );

  return (
    <AppPreferencesContext.Provider value={value}>
      {children}
    </AppPreferencesContext.Provider>
  );
}

export function useAppPreferences() {
  const context = useContext(AppPreferencesContext);

  if (!context) {
    throw new Error(
      "useAppPreferences must be used inside AppPreferencesProvider"
    );
  }

  return context;
}